// src/pages/accounting/ProjectExpensesPage.jsx
// Extracted from App.js - Project Expenses admin page
// BRIO Migration: Using code "1b" (table compact)
// CURRENCY: Per-expense INR/EUR input toggle + display currency from useSettings
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { AlertTriangle, ChevronUp, ChevronDown, X } from 'lucide-react'; // eslint-disable-line no-unused-vars
import {
  BrioAnalyticsSurface,
  BrioFrame,
  BrioByCode,
  BrioTable,
  BrioIconButton,
  BrioDatePicker,
  BrioSelect,
  BrioModal,
  getTablePropsFromCode
} from '../../components/BrioComponents';
import { useSettings } from '../../hooks/useSettings';
import { OFFICE_OVERHEAD_CATEGORIES, PROJECT_EXPENSE_CATEGORIES } from '../../utils/expenseShared';


// Normalize legacy office location values (Rotterdam → NL, Mumbai → IN)
const normalizeOfficeLocation = (loc) => {
  if (!loc) return 'NL';
  if (loc === 'Rotterdam') return 'NL';
  if (loc === 'Mumbai') return 'IN';
  return loc;
};

// ── CSV parsing utilities ─────────────────────────────────────────────────

/**
 * Parse a single CSV line respecting RFC 4180 quoted fields.
 * Handles commas/tabs/semicolons inside quoted fields and escaped quotes ("").
 */
function parseCSVLine(line, delimiter) {
  const fields = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i++; // skip escaped quote
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === delimiter) {
      fields.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current.trim());
  return fields;
}

/**
 * Auto-detect delimiter by counting occurrences in non-quoted portions of lines.
 * Returns '\t', ';', or ','.
 */
function detectDelimiter(lines) {
  // Only inspect up to 10 lines for detection
  const sample = lines.slice(0, Math.min(lines.length, 10));
  const candidates = ['\t', ';', ','];
  const scores = candidates.map(delim => {
    let total = 0;
    for (const line of sample) {
      let inQuotes = false;
      let count = 0;
      for (let i = 0; i < line.length; i++) {
        if (line[i] === '"') inQuotes = !inQuotes;
        else if (!inQuotes && line[i] === delim) count++;
      }
      total += count;
    }
    return total;
  });
  const maxIdx = scores.indexOf(Math.max(...scores));
  return candidates[maxIdx] || ',';
}

/**
 * Parse European-format number: "26.437,00" → 26437.00
 * Also handles plain numbers like "50000" or "50000.00".
 */
function parseEuropeanNumber(str) {
  if (!str) return NaN;
  const s = str.replace(/\s/g, ''); // strip whitespace
  // European: dots as thousands, comma as decimal (e.g. "26.437,00")
  if (/^\d{1,3}(\.\d{3})*(,\d+)?$/.test(s)) {
    return parseFloat(s.replace(/\./g, '').replace(',', '.'));
  }
  // Indian/US: commas as thousands, dot as decimal (e.g. "26,437.00")
  if (/^\d{1,3}(,\d{2,3})*(\.\d+)?$/.test(s)) {
    return parseFloat(s.replace(/,/g, ''));
  }
  // Plain number
  return parseFloat(s);
}

/**
 * Parse various date formats into ISO YYYY-MM-DD.
 * Supports: DD/MM/YYYY, DD-MM-YYYY, DD.MM.YYYY, DD-MMM-YYYY, YYYY-MM-DD, MM/DD/YYYY (if year > 12)
 */
function parseDateFlexible(str) {
  if (!str || !str.trim()) return null;
  const s = str.trim();
  // Already ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  // DD/MM/YYYY or DD-MM-YYYY or DD.MM.YYYY
  const dmy = s.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})$/);
  if (dmy) {
    const [, dd, mm, yyyy] = dmy;
    return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
  }
  // DD-MMM-YYYY (e.g. "06-Mar-2026")
  const months = { jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06', jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12' };
  const dmmmy = s.match(/^(\d{1,2})[/\-.]([A-Za-z]{3})[/\-.](\d{4})$/);
  if (dmmmy) {
    const [, dd, mmm, yyyy] = dmmmy;
    const mm = months[mmm.toLowerCase()];
    if (mm) return `${yyyy}-${mm}-${dd.padStart(2, '0')}`;
  }
  return null;
}

/**
 * Detect if the CSV is a bank export format (HDFC-style with metadata header rows).
 * Returns { isBankFormat, dataStartLine, headerLine } or null.
 */
function detectBankFormat(lines, delimiter) {
  // Bank CSVs typically have metadata rows before the actual header.
  // Look for a header row containing bank-specific columns like "UTR", "Beneficiary", "Txn"
  const bankHeaderKeywords = ['utr', 'beneficiary', 'txn'];
  for (let i = 0; i < Math.min(lines.length, 10); i++) {
    const lower = lines[i].toLowerCase();
    const matchCount = bankHeaderKeywords.filter(kw => lower.includes(kw)).length;
    if (matchCount >= 2) {
      return { isBankFormat: true, headerLine: i, dataStartLine: i + 1 };
    }
  }
  return null;
}

/**
 * Build a duplicate-check key from expense fields.
 */
function expenseDuplicateKey(exp) {
  return `${exp.projectId || ''}|${exp.date}|${exp.amountOriginal}|${(exp.description || '').toLowerCase().trim()}`;
}

// Presentation code for this page
const PRESENTATION_CODE = '1b';

// ── Inline currency toggle (compact, BRIO gold) ──────────────────────────
function CurrencyToggle({ value = 'INR', onChange }) {
  const btn = (c) => ({
    border: 'none', cursor: 'pointer', fontFamily: "'Raleway', sans-serif",
    fontWeight: 600, fontSize: 10, padding: '2px 7px', lineHeight: 1.4,
    letterSpacing: '0.02em', transition: 'background 0.15s, color 0.15s',
    background: value === c ? 'var(--brio-gold, #c5a572)' : '#fff',
    color: value === c ? '#fff' : '#888',
  });
  return (
    <div style={{ display: 'inline-flex', borderRadius: 0, overflow: 'hidden', border: '1px solid var(--brio-border, #d4d0c8)', flexShrink: 0 }}>
      <button type="button" style={btn('INR')} onClick={() => onChange('INR')} title="Indian Rupees">₹</button>
      <button type="button" style={btn('EUR')} onClick={() => onChange('EUR')} title="Euros">€</button>
    </div>
  );
}

export default function ProjectExpensesPage({
  // Data
  activeProjects,
  activeExpenses,
  projectExpenses,
  setProjectExpenses,
  // Form state
  form,
  setForm,
  // Supabase
  useSupabaseDB,
  supabaseExpenses,
  supabaseOfficeOverheads,
  // Formatters (legacy)
  fmtCurr,
  fmtProject,
  currSymbol = '₹',
  // Project filter
  getSortedProjects,
  // Reimbursements (project-linked claims)
  reimbursementsHook,
  // Invoices hook — for client reimbursement deductions
  invoicesHook,
}) {
  const tableProps = getTablePropsFromCode(PRESENTATION_CODE);
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  // ── Filters (persisted to localStorage) ──
  const _loadPEFilters = () => { try { return JSON.parse(localStorage.getItem('brio_project_expenses_filters') || '{}'); } catch { return {}; } };
  const [filterYear,       setFilterYear]       = useState(() => _loadPEFilters().year       || '');
  const [filterDateFrom,   setFilterDateFrom]   = useState(() => _loadPEFilters().dateFrom   || '');
  const [filterDateTo,     setFilterDateTo]     = useState(() => _loadPEFilters().dateTo     || '');
  const [filterSourceType, setFilterSourceType] = useState(() => _loadPEFilters().sourceType || '');
  const [filterProject,    setFilterProject]    = useState(() => _loadPEFilters().project    || '');
  const [filterOffice,     setFilterOffice]     = useState(() => _loadPEFilters().office     || '');
  const [filterBillable,   setFilterBillable]   = useState(() => _loadPEFilters().billable   || '');

  useEffect(() => {
    try { localStorage.setItem('brio_project_expenses_filters', JSON.stringify({ year: filterYear, dateFrom: filterDateFrom, dateTo: filterDateTo, sourceType: filterSourceType, project: filterProject, office: filterOffice, billable: filterBillable })); } catch {}
  }, [filterYear, filterDateFrom, filterDateTo, filterSourceType, filterProject, filterOffice, filterBillable]);

  // ── CSV import review modal state ──
  const [csvReviewRows, setCsvReviewRows] = useState(null);   // null = modal closed; [] = open
  const [csvReviewErrors, setCsvReviewErrors] = useState([]);
  const [csvReviewSkipped, setCsvReviewSkipped] = useState([]);
  const [csvImporting, setCsvImporting] = useState(false);

  // ── Inline edit state ──
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [editingExpenseData, setEditingExpenseData] = useState({});

  // ── Currency settings from useSettings hook (direct call — self-sufficient) ──
  const settings = useSettings();
  const essentialProfitPercentage = settings.essentialProfitPercentage ?? 20;
  const displayCurrency = settings.displayCurrency || 'INR';
  const getFxRate = settings.getFxRateForDate;
  const convertMoney = settings.convertMoney;
  const formatMoney = settings.formatMoney || fmtCurr || ((v) => String(v));
  const fxRateMap = settings.fxRateMap || new Map();

  const displaySymbol = displayCurrency === 'EUR' ? '€' : '₹';

  // ── Filter project dropdowns using the global project status filter ──
  const dropdownProjects = useMemo(
    () => getSortedProjects ? getSortedProjects() : activeProjects.filter(p => p.status === 'active' || p.status === 'paused'),
    [activeProjects, getSortedProjects]
  );

  // ── Project-linked reimbursements mapped into expense-compatible shape ──
  const projectReimbursements = useMemo(() => {
    if (!reimbursementsHook?.reimbursements) return [];
    return reimbursementsHook.reimbursements
      .filter(r => r.projectId && !r.isOfficeExpense)
      .map(r => {
        // For EUR claims where amountInInr was not converted, compute INR amount
        const origAmt = r.amount ?? 0;
        const origCur = r.currency || 'INR';
        const hasValidInr = r.amountInInr != null && (origCur === 'INR' || Number(r.amountInInr) !== Number(origAmt));
        const inrAmt = hasValidInr ? r.amountInInr : origAmt;
        return {
        id: `reimb-${r.id}`,
        _reimbursementId: r.id,
        _isReimbursement: true,
        projectId: r.projectId,
        category: r.category || 'Miscellaneous',
        description: `${r.vendor ? r.vendor + ' — ' : ''}${r.description || ''}`.trim() || '—',
        amount: inrAmt,
        amountInInr: inrAmt,
        amountOriginal: origAmt,
        currencyOriginal: origCur,
        _needsEurConversion: origCur === 'EUR' && !hasValidInr,
        fxRateToInr: r.fxRateToInr ?? 1,
        date: r.expenseDate,
        status: r.status || 'pending',
        officeLocation: r.officeLocation || 'IN',
        _claimStatus: r.status,
        _isLinkedReceipt: !!r.linkedExpenseId,
      };
      });
  }, [reimbursementsHook?.reimbursements]);

  // ── Combined: expenses + project reimbursements ──
  const combinedExpenses = useMemo(() => {
    const tagged = activeExpenses.filter(e => !e.isOfficeExpense).map(e => ({ ...e, _isReimbursement: false }));
    return [...tagged, ...projectReimbursements];
  }, [activeExpenses, projectReimbursements]);

  // ── Available years: 2018 → current (plus any data years outside that range) ──
  const availableYears = useMemo(() => {
    const years = new Set();
    const currentYear = new Date().getFullYear();
    for (let y = 2018; y <= currentYear; y++) years.add(String(y));
    combinedExpenses.forEach(e => { if (e.date) years.add(e.date.substring(0, 4)); });
    return [...years].sort((a, b) => b.localeCompare(a));
  }, [combinedExpenses]);

  // ── Filtered view ──
  const filteredCombined = useMemo(() => {
    return combinedExpenses.filter(e => {
      if (filterYear       && e.date && !e.date.startsWith(filterYear))               return false;
      if (filterDateFrom   && e.date && e.date < filterDateFrom)                      return false;
      if (filterDateTo     && e.date && e.date > filterDateTo)                        return false;
      if (filterProject    && String(e.projectId) !== String(filterProject))          return false;
      if (filterOffice     && e.officeLocation !== filterOffice)                      return false;
      if (filterBillable === 'yes' && !e.clientBillable)                             return false;
      if (filterBillable === 'no'  && e.clientBillable)                              return false;
      if (filterSourceType) {
        const isBankImport = e.importSource?.startsWith('bank-import');
        const isReceipt    = e._isReimbursement;
        if (filterSourceType === 'bank-import' && !isBankImport)                     return false;
        if (filterSourceType === 'receipt'     && !isReceipt)                        return false;
        if (filterSourceType === 'manual'      && (isBankImport || isReceipt))       return false;
      }
      return true;
    });
  }, [combinedExpenses, filterYear, filterDateFrom, filterDateTo, filterSourceType, filterProject, filterOffice, filterBillable]);

  // ── Display helpers ──
  const displayAmount = (exp) => {
    // For EUR reimbursements where amountInInr was not converted, convert from EUR
    if (exp._needsEurConversion) {
      return convertMoney(exp.amountOriginal || exp.amount || 0, 'EUR', displayCurrency, exp.date);
    }
    const baseInr = exp.amountInInr ?? exp.amount ?? 0;
    return convertMoney(baseInr, 'INR', displayCurrency, exp.date);
  };
  const fmtDisplay = (exp) => formatMoney(displayAmount(exp), displayCurrency, exp?._isReimbursement ? { exact: true } : undefined);

  // ── Currency input state ──
  const inputCurrency = form.newExpenseCurrency || 'INR';
  const inputDate = form.newExpenseDate || new Date().toISOString().split('T')[0];

  const fxHelper = useMemo(() => {
    if (inputCurrency === 'INR') return null;
    if (!getFxRate) return { text: 'Currency settings not loaded yet', warn: true };
    const monthKey = inputDate.substring(0, 7);
    const [y, m] = monthKey.split('-');
    const label = new Date(+y, +m - 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
    const rate = getFxRate(inputDate);
    if (!rate || rate <= 1) return { text: `No valid FX rate found for ${label}. Add rates in Settings → FX rates.`, warn: true };
    const isExact = fxRateMap.has(monthKey);
    if (isExact) {
      return { text: `Avg rate ${label}: 1 EUR = ${rate} INR`, warn: false };
    }
    return { text: `No rate for ${label} — using nearest: 1 EUR = ${rate} INR`, warn: false };
  }, [inputCurrency, inputDate, fxRateMap, getFxRate]);

  // Sort
  const sortedExpenses = [...filteredCombined].sort((a, b) => {
    const projectA = activeProjects.find(p => p.id === a.projectId);
    const projectB = activeProjects.find(p => p.id === b.projectId);
    let valA, valB;
    switch(sortConfig.key) {
      case 'project':
        valA = (projectA?.name || '').toLowerCase(); valB = (projectB?.name || '').toLowerCase();
        return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case 'category':
        valA = (a.category || '').toLowerCase(); valB = (b.category || '').toLowerCase();
        return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case 'description':
        valA = (a.description || '').toLowerCase(); valB = (b.description || '').toLowerCase();
        return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case 'amount':
        valA = a.amountInInr ?? a.amount ?? 0; valB = b.amountInInr ?? b.amount ?? 0;
        return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
      case 'date':
        valA = new Date(a.date || '1900-01-01'); valB = new Date(b.date || '1900-01-01');
        return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
      case 'status':
        valA = (a.status || '').toLowerCase(); valB = (b.status || '').toLowerCase();
        return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      case 'office':
        valA = normalizeOfficeLocation(a.officeLocation).toLowerCase(); valB = normalizeOfficeLocation(b.officeLocation).toLowerCase();
        return sortConfig.direction === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
      default: return 0;
    }
  });

  const handleSortClick = (sortKey) => {
    const newDirection = sortConfig.key === sortKey && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key: sortKey, direction: newDirection });
  };
  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  // ── Client reimbursements (Option A: bank_payment_events linked to projects) ──
  useEffect(() => {
    invoicesHook?.fetchExpensePayments?.();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clientReimbursements = useMemo(() => {
    const payments = (invoicesHook?.expensePayments || []).filter(p => p.linkedProjectId);
    return payments.filter(p => {
      if (filterProject && String(p.linkedProjectId) !== String(filterProject)) return false;
      if (filterYear     && p.txnDate && !p.txnDate.startsWith(filterYear))    return false;
      if (filterDateFrom && p.txnDate && p.txnDate < filterDateFrom)           return false;
      if (filterDateTo   && p.txnDate && p.txnDate > filterDateTo)             return false;
      return true;
    });
  }, [invoicesHook?.expensePayments, filterProject, filterYear, filterDateFrom, filterDateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  const grossTotal = useMemo(() =>
    filteredCombined.filter(e => !e._isLinkedReceipt).reduce((s, e) => s + displayAmount(e), 0),
  [filteredCombined]); // eslint-disable-line react-hooks/exhaustive-deps

  const clientDeduction = useMemo(() =>
    clientReimbursements.reduce((s, p) => {
      const amt = convertMoney ? convertMoney(p.amount, 'INR', displayCurrency, p.txnDate) : p.amount;
      return s + amt;
    }, 0),
  [clientReimbursements, convertMoney, displayCurrency]);

  // ── CSV review modal: update a row field ──
  const updateReviewRow = useCallback((index, field, value) => {
    setCsvReviewRows(prev => {
      if (!prev) return prev;
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      // When category changes to "Office Expenses", flag it
      if (field === '_reviewCategory' && value === 'Office Expenses') {
        updated[index]._reviewIsOffice = true;
        updated[index]._reviewProjectId = '';
      } else if (field === '_reviewCategory' && value !== 'Office Expenses') {
        updated[index]._reviewIsOffice = false;
      }
      return updated;
    });
  }, []);

  // ── CSV review modal: remove a row ──
  const removeReviewRow = useCallback((index) => {
    setCsvReviewRows(prev => {
      if (!prev) return prev;
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // ── CSV review modal: apply project/category to all rows at once ──
  const applyToAllRows = useCallback((field, value) => {
    setCsvReviewRows(prev => {
      if (!prev) return prev;
      return prev.map(row => {
        const updated = { ...row, [field]: value };
        if (field === '_reviewCategory' && value === 'Office Expenses') {
          updated._reviewIsOffice = true;
          updated._reviewProjectId = '';
        } else if (field === '_reviewCategory' && value !== 'Office Expenses') {
          updated._reviewIsOffice = false;
        }
        return updated;
      });
    });
  }, []);

  // ── CSV review modal: confirm import ──
  const handleConfirmImport = useCallback(async () => {
    if (!csvReviewRows || csvReviewRows.length === 0) return;

    // Validate: each row needs category; project rows need a project
    const validationErrors = [];
    csvReviewRows.forEach((row, i) => {
      if (!row._reviewCategory) {
        validationErrors.push(`Row ${i + 1}: Please select a category`);
      }
      if (!row._reviewIsOffice && !row._reviewProjectId) {
        validationErrors.push(`Row ${i + 1} ("${row.description?.substring(0, 30)}"): Please select a project`);
      }
    });
    if (validationErrors.length > 0) {
      alert('Please fix before importing:\n' + validationErrors.slice(0, 10).join('\n'));
      return;
    }

    setCsvImporting(true);
    const errors = [];

    // Split into project expenses vs office expenses
    const projectRows = csvReviewRows.filter(r => !r._reviewIsOffice);
    const officeRows = csvReviewRows.filter(r => r._reviewIsOffice);

    // ── Insert project expenses ──
    if (projectRows.length > 0) {
      const expensesToInsert = projectRows.map(r => ({
        projectId: Number(r._reviewProjectId),
        category: r._reviewCategory,
        description: r.description,
        amount: r.amountInInr,
        date: r.date,
        status: r.status || 'planned',
        officeLocation: r.officeLocation,
        amountOriginal: r.amountOriginal,
        currencyOriginal: r.currencyOriginal,
        fxRateToInr: r.fxRateToInr,
        amountInInr: r.amountInInr,
      }));

      if (useSupabaseDB) {
        if (supabaseExpenses.createExpenses) {
          const result = await supabaseExpenses.createExpenses(expensesToInsert);
          if (!result.success) errors.push('Project expenses bulk insert error: ' + (result.error || 'Unknown'));
        } else {
          for (const exp of expensesToInsert) {
            const result = await supabaseExpenses.createExpense(exp);
            if (!result.success) errors.push('Failed to insert: ' + (exp.description || '').substring(0, 40));
          }
        }
      } else {
        setProjectExpenses(prev => [...prev, ...expensesToInsert]);
      }
    }

    // ── Insert office expenses ──
    if (officeRows.length > 0 && supabaseOfficeOverheads) {
      for (const r of officeRows) {
        const year = r.date ? new Date(r.date).getFullYear() : new Date().getFullYear();
        const overhead = {
          year,
          category: 'Office Operations',  // default office overhead category
          subcategory: r._reviewOfficeSubcategory || r.description || 'CSV Import',
          amount: r.amountInInr,
          officeLocation: r.officeLocation,
          expenseDate: r.date || new Date().toISOString().split('T')[0],
          amountOriginal: r.amountOriginal,
          currencyOriginal: r.currencyOriginal,
          fxRateToInr: r.fxRateToInr,
          amountInInr: r.amountInInr,
        };
        const result = await supabaseOfficeOverheads.createOfficeOverhead(overhead);
        if (!result.success) errors.push('Office expense error: ' + (r.description || '').substring(0, 40));
      }
    } else if (officeRows.length > 0 && !supabaseOfficeOverheads) {
      errors.push(`${officeRows.length} office expense(s) could not be saved — office overheads hook unavailable`);
    }

    setCsvImporting(false);

    // Build summary
    const parts = ['Import complete!'];
    if (projectRows.length > 0) parts.push(`Project expenses: ${projectRows.length}`);
    if (officeRows.length > 0) parts.push(`Office expenses: ${officeRows.length}`);
    if (errors.length > 0) {
      parts.push(`Errors: ${errors.length}`);
      parts.push(errors.slice(0, 10).join('\n'));
    }
    alert(parts.join('\n'));
    setCsvReviewRows(null);
    setCsvReviewErrors([]);
    setCsvReviewSkipped([]);
  }, [csvReviewRows, useSupabaseDB, supabaseExpenses, supabaseOfficeOverheads, setProjectExpenses]);

  return (
    <div className="brio-flex brio-flex-col brio-analytics-font" style={{ background: '#fff' }}>
      {/* ── CSV Import Review Modal ── */}
      {csvReviewRows !== null && (
        <BrioModal
          onClose={() => { setCsvReviewRows(null); setCsvReviewErrors([]); setCsvReviewSkipped([]); }}
          title="Review CSV Import"
          width="xl"
        >
          <div style={{ maxHeight: '70vh', overflow: 'auto', padding: '12px 0' }}>
            {/* Parse warnings */}
            {(csvReviewErrors.length > 0 || csvReviewSkipped.length > 0) && (
              <div style={{ background: '#fffcf0', border: '1px solid var(--brio-gold)', padding: 10, marginBottom: 12, fontSize: 11 }}>
                {csvReviewSkipped.length > 0 && (
                  <div style={{ marginBottom: 4 }}>Skipped {csvReviewSkipped.length} duplicate(s)</div>
                )}
                {csvReviewErrors.length > 0 && (
                  <div style={{ color: '#c0392b' }}>
                    {csvReviewErrors.length} parse error(s): {csvReviewErrors.slice(0, 3).join('; ')}
                    {csvReviewErrors.length > 3 && ` … +${csvReviewErrors.length - 3} more`}
                  </div>
                )}
              </div>
            )}

            {/* Bulk assign controls */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--brio-gold, #c5a572)' }}>Apply to all rows:</span>
              <select
                className="brio-btn brio-btn-secondary"
                style={{ padding: '4px 8px', fontSize: '11px' }}
                value=""
                onChange={(e) => { if (e.target.value) applyToAllRows('_reviewProjectId', e.target.value); }}
              >
                <option value="">Set project…</option>
                {dropdownProjects.map(p => <option key={p.id} value={p.id}>{fmtProject(p)}</option>)}
              </select>
              <select
                className="brio-btn brio-btn-secondary"
                style={{ padding: '4px 8px', fontSize: '11px' }}
                value=""
                onChange={(e) => { if (e.target.value) applyToAllRows('_reviewCategory', e.target.value); }}
              >
                <option value="">Set category…</option>
                {PROJECT_EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                <option disabled>──────────</option>
                <option value="Office Expenses">Office Expenses</option>
              </select>
            </div>

            {/* Row table */}
            <BrioByCode code={PRESENTATION_CODE}>
              <BrioTable {...tableProps} style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '25%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '6%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Description</th>
                    <th className="text-right">Amount</th>
                    <th className="text-center">Date</th>
                    <th>Project</th>
                    <th>Category</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {csvReviewRows.map((row, idx) => {
                    const truncCell = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 };
                    const cellSel = { width: '100%', padding: '2px 4px', fontSize: '10px', boxSizing: 'border-box' };
                    return (
                    <tr key={idx} style={{ background: row._reviewIsOffice ? '#f0f7ff' : undefined }}>
                      <td style={{ fontSize: 10, color: '#999' }}>{idx + 1}</td>
                      <td style={{ ...truncCell, fontSize: 10 }} title={row.description}>{row.description}</td>
                      <td className="text-right" style={{ ...truncCell, fontSize: 10 }}>
                        {row.currencyOriginal === 'EUR' ? '€' : '₹'}{row.amountOriginal?.toLocaleString()}
                      </td>
                      <td className="text-center" style={{ ...truncCell, fontSize: 10 }}>{row.date}</td>
                      <td>
                        {row._reviewIsOffice ? (
                          <span style={{ fontSize: 9, color: '#666', fontStyle: 'italic' }}>N/A (office)</span>
                        ) : (
                          <select
                            value={row._reviewProjectId || ''}
                            className="brio-btn brio-btn-secondary"
                            style={cellSel}
                            onChange={(e) => updateReviewRow(idx, '_reviewProjectId', e.target.value)}
                          >
                            <option value="">Project…</option>
                            {dropdownProjects.map(p => <option key={p.id} value={p.id}>{fmtProject(p)}</option>)}
                          </select>
                        )}
                      </td>
                      <td>
                        <select
                          value={row._reviewCategory || ''}
                          className="brio-btn brio-btn-secondary"
                          style={cellSel}
                          onChange={(e) => updateReviewRow(idx, '_reviewCategory', e.target.value)}
                        >
                          <option value="">Select…</option>
                          {PROJECT_EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                          <option disabled>──────────</option>
                          <option value="Office Expenses">Office Exp.</option>
                        </select>
                      </td>
                      <td className="text-center">
                        <button
                          onClick={() => removeReviewRow(idx)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c0392b', padding: 2 }}
                          title="Remove row"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </BrioTable>
            </BrioByCode>

            {csvReviewRows.length === 0 && (
              <div className="brio-text-center brio-analytics-muted" style={{ padding: '24px 0', fontSize: 12 }}>
                All rows removed. Close this dialog or re-import.
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--brio-border, #d4d0c8)', paddingTop: 12, marginTop: 8 }}>
            <span style={{ fontSize: 11, color: '#666' }}>
              {csvReviewRows.length} row(s) — {csvReviewRows.filter(r => r._reviewIsOffice).length} office, {csvReviewRows.filter(r => !r._reviewIsOffice).length} project
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="brio-btn brio-btn-secondary"
                style={{ padding: '6px 16px', fontSize: '12px' }}
                onClick={() => { setCsvReviewRows(null); setCsvReviewErrors([]); setCsvReviewSkipped([]); }}
              >
                Cancel
              </button>
              <button
                className="brio-btn brio-btn-primary"
                style={{ padding: '6px 16px', fontSize: '12px' }}
                disabled={csvImporting || csvReviewRows.length === 0}
                onClick={handleConfirmImport}
              >
                {csvImporting ? 'Importing…' : `Import ${csvReviewRows.length} row(s)`}
              </button>
            </div>
          </div>
        </BrioModal>
      )}

      {/* PAGE HEADER BAND */}
      <div className="brio-pageband brio-pageband--header">
        <BrioFrame>
          <div className="brio-page-header__content">
            <div className="brio-flex brio-items-center brio-gap-xs" style={{ flexWrap: 'nowrap' }}>
              <BrioSelect value={filterYear} onChange={e => { setFilterYear(e.target.value); if (e.target.value) { setFilterDateFrom(''); setFilterDateTo(''); } }}
                style={{ padding: '4px 8px', fontSize: '11px', minWidth: 70 }}>
                <option value="">All Years</option>
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </BrioSelect>
              <BrioDatePicker value={filterDateFrom} onChange={v => { setFilterDateFrom(v); if (v) setFilterYear(''); }}
                className="brio-datepicker-trigger--compact" placeholder="From"
                style={{ width: 110, fontSize: '10px' }} clearable />
              <BrioDatePicker value={filterDateTo} onChange={v => { setFilterDateTo(v); if (v) setFilterYear(''); }}
                className="brio-datepicker-trigger--compact" placeholder="To"
                style={{ width: 110, fontSize: '10px' }} clearable />
              <BrioSelect value={filterSourceType} onChange={e => setFilterSourceType(e.target.value)}
                style={{ padding: '4px 8px', fontSize: '11px', minWidth: 110 }}>
                <option value="">All Types</option>
                <option value="bank-import">Bank Import</option>
                <option value="receipt">Receipt</option>
                <option value="manual">Manual</option>
              </BrioSelect>
              <BrioSelect value={filterProject} onChange={e => setFilterProject(e.target.value)}
                style={{ padding: '4px 8px', fontSize: '11px' }}>
                <option value="">All Projects</option>
                {dropdownProjects.map(p => <option key={p.id} value={p.id}>{p.projectCode ? `${p.projectCode} ${p.name}` : p.name}</option>)}
              </BrioSelect>
              <BrioSelect value={filterOffice} onChange={e => setFilterOffice(e.target.value)}
                style={{ padding: '4px 8px', fontSize: '11px', minWidth: 70 }}>
                <option value="">All Offices</option>
                <option value="NL">NL</option>
                <option value="IN">IN</option>
              </BrioSelect>
              <BrioSelect value={filterBillable} onChange={e => setFilterBillable(e.target.value)}
                style={{ padding: '4px 8px', fontSize: '11px', minWidth: 90 }}>
                <option value="">All Billing</option>
                <option value="yes">Client-Billable</option>
                <option value="no">Non-Billable</option>
              </BrioSelect>
            </div>
          </div>
        </BrioFrame>
      </div>

      {/* CONTENT AREA */}
      <BrioFrame>
        <div className="brio-flex brio-flex-col brio-gap-s" style={{ paddingTop: 'var(--brio-space-s)' }}>
          <BrioAnalyticsSurface padding="m">
            <BrioByCode code={PRESENTATION_CODE}>
              <BrioTable {...tableProps} style={{ tableLayout: 'fixed', width: '100%' }}>
                <colgroup>
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '8%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '8%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('project')}>Project{getSortIndicator('project')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('category')}>Category{getSortIndicator('category')}</th>
                    <th style={{ cursor: 'pointer' }} onClick={() => handleSortClick('description')}>Description{getSortIndicator('description')}</th>
                    <th className="text-right" style={{ cursor: 'pointer' }} onClick={() => handleSortClick('amount')}>Amount ({displaySymbol}){getSortIndicator('amount')}</th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSortClick('date')}>Date{getSortIndicator('date')}</th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSortClick('status')}>Status{getSortIndicator('status')}</th>
                    <th className="text-center" style={{ cursor: 'pointer' }} onClick={() => handleSortClick('office')}>Office{getSortIndicator('office')}</th>
                    <th className="text-center" style={{ width: 70 }}>Source</th>
                    <th className="text-center col-actions">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Existing expenses */}
                  {sortedExpenses.map(exp => {
                    const project = activeProjects.find(p => p.id === exp.projectId);
                    const isEditing = editingExpenseId === exp.id;

                    if (isEditing) {
                      const ed = editingExpenseData;
                      const edCurrency = ed.currencyOriginal || 'INR';
                      const edDate = ed.date || new Date().toISOString().split('T')[0];
                      const edFxHelper = (() => {
                        if (edCurrency === 'INR') return null;
                        if (!getFxRate) return { text: 'Currency settings not loaded', warn: true };
                        const mk = edDate.substring(0, 7);
                        const [ey, em] = mk.split('-');
                        const lbl = new Date(+ey, +em - 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
                        const r = getFxRate(edDate);
                        if (!r || r <= 1) return { text: `No FX rate for ${lbl}`, warn: true };
                        return { text: `1 EUR = ${r} INR (${lbl})`, warn: false };
                      })();
                      const cellInput = { width: '100%', padding: '3px 4px', fontSize: '10px', boxSizing: 'border-box' };
                      return (
                        <tr key={exp.id} style={{ backgroundColor: 'var(--brio-gold-light)' }}>
                          <td>
                            <select value={ed.projectId || ''} onChange={(e) => setEditingExpenseData({...ed, projectId: e.target.value})}
                              className="brio-btn brio-btn-secondary" style={cellInput}>
                              <option value="">Select...</option>
                              {dropdownProjects.map(p => <option key={p.id} value={p.id}>{fmtProject(p)}</option>)}
                            </select>
                          </td>
                          <td>
                            <select value={ed.category || ''} onChange={(e) => setEditingExpenseData({...ed, category: e.target.value})}
                              className="brio-btn brio-btn-secondary" style={cellInput}>
                              {PROJECT_EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </td>
                          <td>
                            <input type="text" value={ed.description || ''} onChange={(e) => setEditingExpenseData({...ed, description: e.target.value})}
                              className="brio-btn brio-btn-secondary" style={cellInput} />
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <input type="number" value={ed.amountOriginal ?? ''} onChange={(e) => setEditingExpenseData({...ed, amountOriginal: e.target.value})}
                                  className="brio-btn brio-btn-secondary"
                                  style={{ ...cellInput, flex: 1, minWidth: 0, textAlign: 'right' }} />
                                <CurrencyToggle value={edCurrency} onChange={(c) => setEditingExpenseData({...ed, currencyOriginal: c})} />
                              </div>
                              {edFxHelper && (
                                <span style={{ fontSize: 8, color: edFxHelper.warn ? '#c0392b' : '#999', fontStyle: 'italic' }}>
                                  {edFxHelper.warn ? '\u26a0 ' : ''}{edFxHelper.text}
                                </span>
                              )}
                            </div>
                          </td>
                          <td>
                            <BrioDatePicker value={edDate} onChange={(v) => setEditingExpenseData({...ed, date: v})}
                              openAbove
                              style={{ width: '100%', maxWidth: '95px', fontSize: '10px', boxSizing: 'border-box' }}
                              className="brio-datepicker-trigger--compact" />
                          </td>
                          <td>
                            <select value={ed.status || 'planned'} onChange={(e) => setEditingExpenseData({...ed, status: e.target.value})}
                              className="brio-btn brio-btn-secondary" style={cellInput}>
                              <option value="planned">Planned</option>
                              <option value="committed">Committed</option>
                              <option value="invoiced">Invoiced</option>
                              <option value="paid">Paid</option>
                            </select>
                          </td>
                          <td>
                            <select value={ed.officeLocation || 'NL'} onChange={(e) => setEditingExpenseData({...ed, officeLocation: e.target.value})}
                              className="brio-btn brio-btn-secondary" style={cellInput}>
                              <option value="NL">NL</option>
                              <option value="IN">IN</option>
                            </select>
                            <label
                              title="Vendor established outside the office country (triggers reverse charge for SaaS, ads, professional services)"
                              style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, marginTop: 2, cursor: 'pointer', color: 'var(--brio-text-secondary)' }}>
                              <input type="checkbox" checked={!!ed.foreignVendor}
                                onChange={(e) => setEditingExpenseData({...ed, foreignVendor: e.target.checked})}
                                style={{ margin: 0 }} />
                              Foreign
                            </label>
                          </td>
                          <td></td>
                          <td className="text-center">
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                              <button
                                className="brio-btn brio-btn-primary"
                                style={{ padding: '3px 8px', fontSize: '10px' }}
                                onClick={async () => {
                                  const amountOrig = parseFloat(ed.amountOriginal);
                                  if (!Number.isFinite(amountOrig) || amountOrig <= 0) { alert('Please enter a valid amount'); return; }
                                  const projId = Number(ed.projectId);
                                  if (!Number.isFinite(projId) || projId <= 0) { alert('Please select a project'); return; }

                                  let fxRate = 1;
                                  let amtInr = amountOrig;
                                  if (ed.currencyOriginal === 'EUR') {
                                    fxRate = getFxRate ? getFxRate(ed.date) : 0;
                                    if (!fxRate || fxRate <= 1) { alert('No valid FX rate for this date'); return; }
                                    amtInr = Math.round(amountOrig * fxRate * 100) / 100;
                                  }

                                  const updates = {
                                    projectId: projId,
                                    category: ed.category,
                                    description: ed.description,
                                    amount: amtInr,
                                    date: ed.date,
                                    status: ed.status,
                                    officeLocation: ed.officeLocation,
                                    amountOriginal: amountOrig,
                                    currencyOriginal: ed.currencyOriginal || 'INR',
                                    fxRateToInr: fxRate,
                                    amountInInr: amtInr,
                                    foreignVendor: !!ed.foreignVendor,
                                  };

                                  if (useSupabaseDB) {
                                    const result = await supabaseExpenses.updateExpense(exp.id, updates);
                                    if (!result.success) { alert('Error updating expense: ' + result.error); return; }
                                  } else {
                                    setProjectExpenses(projectExpenses.map(e => e.id === exp.id ? { ...e, ...updates } : e));
                                  }
                                  setEditingExpenseId(null);
                                  setEditingExpenseData({});
                                }}
                              >Save</button>
                              <button
                                className="brio-btn brio-btn-secondary"
                                style={{ padding: '3px 8px', fontSize: '10px' }}
                                onClick={() => { setEditingExpenseId(null); setEditingExpenseData({}); }}
                              >Cancel</button>
                            </div>
                          </td>
                        </tr>
                      );
                    }

                    const showOrig = exp.currencyOriginal && exp.currencyOriginal !== displayCurrency && exp.amountOriginal != null;
                    const truncStyle = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0 };
                    const isReimb = exp._isReimbursement;
                    const claimStatusBadge = isReimb ? (
                      exp._claimStatus === 'reimbursed' ? 'brio-badge--approved' :
                      exp._claimStatus === 'approved' ? 'brio-badge--warning' :
                      exp._claimStatus === 'voucher' ? 'brio-badge--info' :
                      'brio-badge--pending'
                    ) : null;
                    return (
                      <tr key={exp.id} className="brio-row--hoverable"
                        style={exp._isLinkedReceipt ? { opacity: 0.55 } : undefined}
                        title={exp._isLinkedReceipt ? 'Linked receipt — not counted in totals' : undefined}>
                        <td className="brio-text-grey" style={truncStyle} title={project?.name}>{project?.name}</td>
                        <td className="brio-text-grey" style={truncStyle}>{exp.category}</td>
                        <td className="brio-text-grey" style={truncStyle} title={exp.description}>
                          {exp.description}
                          {isReimb && (
                            <span className="brio-badge brio-badge--accent" style={{ fontSize: '8px', padding: '0 4px', marginLeft: 4, verticalAlign: 'middle' }}>Claim</span>
                          )}
                        </td>
                        <td className="text-right brio-font-medium">
                          <span>{fmtDisplay(exp)}</span>
                          {showOrig && (
                            <div style={{ fontSize: 9, color: '#999', fontStyle: 'italic' }}>
                              orig: {exp.currencyOriginal === 'EUR' ? '€' : '₹'}{exp.amountOriginal?.toLocaleString()}
                            </div>
                          )}
                        </td>
                        <td className="text-center brio-text-grey" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0, fontSize: '10px' }}>{exp.date}</td>
                        <td className="text-center">
                          {isReimb ? (
                            <span className={`brio-badge ${claimStatusBadge}`} style={{ fontSize: '9px', padding: '1px 5px' }}>
                              {exp._claimStatus}
                            </span>
                          ) : (
                            <span className={`brio-badge ${
                              exp.status === 'paid' ? 'brio-badge--approved' :
                              exp.status === 'invoiced' ? 'brio-badge--pending' :
                              exp.status === 'committed' ? 'brio-badge--warning' :
                              'brio-badge--pending'
                            }`} style={{ fontSize: '9px', padding: '1px 5px' }}>
                              {exp.status}
                            </span>
                          )}
                        </td>
                        <td className="text-center brio-text-grey" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 0, fontSize: '10px' }}>{normalizeOfficeLocation(exp.officeLocation)}</td>
                        <td className="text-center">
                          {exp._isLinkedReceipt ? (
                            <span style={{ fontSize: 8, color: 'var(--brio-teal)', fontStyle: 'italic' }}>Linked</span>
                          ) : isReimb ? (
                            <span className="brio-badge" style={{ fontSize: '8px', padding: '1px 5px', backgroundColor: 'var(--brio-teal-light)', color: 'var(--brio-teal)' }}>Receipt</span>
                          ) : exp.importSource && exp.importSource.startsWith('bank-import') ? (
                            <span className="brio-badge" style={{ fontSize: '8px', padding: '1px 5px', backgroundColor: 'var(--brio-gold)', color: '#fff' }}>Bank Import</span>
                          ) : (
                            <span className="brio-badge" style={{ fontSize: '8px', padding: '1px 5px', backgroundColor: '#ccc', color: '#555' }}>Manual</span>
                          )}
                        </td>
                        <td className="text-center">
                          {isReimb ? (
                            <span style={{ fontSize: 9, color: '#999' }}>—</span>
                          ) : (
                          <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                            <BrioIconButton icon="edit" variant="edit" title="Edit expense"
                              onClick={() => {
                                setEditingExpenseId(exp.id);
                                setEditingExpenseData({
                                  projectId: String(exp.projectId || ''),
                                  category: exp.category || 'Miscellaneous',
                                  description: exp.description || '',
                                  amountOriginal: exp.amountOriginal ?? exp.amount ?? '',
                                  date: exp.date || '',
                                  status: exp.status || 'planned',
                                  officeLocation: normalizeOfficeLocation(exp.officeLocation),
                                  currencyOriginal: exp.currencyOriginal || 'INR',
                                  foreignVendor: !!exp.foreignVendor,
                                });
                              }}
                            />
                            <BrioIconButton icon="delete" variant="delete" title="Delete expense"
                              onClick={async () => {
                                if (useSupabaseDB) {
                                  const result = await supabaseExpenses.deleteExpense(exp.id);
                                  if (!result.success) alert('Error deleting expense: ' + result.error);
                                } else {
                                  setProjectExpenses(projectExpenses.filter(e => e.id !== exp.id));
                                }
                              }}
                            />
                          </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}

                  {/* ── Add new row ── */}
                  {(() => {
                    const cellInput = { width: '100%', padding: '3px 4px', fontSize: '10px', boxSizing: 'border-box' };
                    return (
                  <tr style={{ backgroundColor: 'var(--brio-gold-light)' }}>
                    <td>
                      <select value={form.newExpenseProject || ''} onChange={(e) => setForm({...form, newExpenseProject: e.target.value})}
                        className="brio-btn brio-btn-secondary" style={cellInput}>
                        <option value="">Select...</option>
                        {dropdownProjects.map(p => <option key={p.id} value={p.id}>{fmtProject(p)}</option>)}
                      </select>
                    </td>
                    <td>
                      <select value={form.newExpenseCategory || ''} onChange={(e) => setForm({...form, newExpenseCategory: e.target.value})}
                        className="brio-btn brio-btn-secondary" style={cellInput}>
                        <option value="">Select...</option>
                        {PROJECT_EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td>
                      <input type="text" value={form.newExpenseDesc || ''} onChange={(e) => setForm({...form, newExpenseDesc: e.target.value})}
                        placeholder="Description" className="brio-btn brio-btn-secondary" style={cellInput} />
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                          <input type="number" value={form.newExpenseAmount || ''} onChange={(e) => setForm({...form, newExpenseAmount: e.target.value})}
                            placeholder="0" className="brio-btn brio-btn-secondary"
                            style={{ ...cellInput, flex: 1, minWidth: 0, textAlign: 'right' }} />
                          <CurrencyToggle value={inputCurrency} onChange={(c) => setForm({...form, newExpenseCurrency: c})} />
                        </div>
                        {fxHelper && (
                          <span style={{ fontSize: 8, color: fxHelper.warn ? '#c0392b' : '#999', fontStyle: 'italic' }}>
                            {fxHelper.warn ? '⚠ ' : ''}{fxHelper.text}
                          </span>
                        )}
                      </div>
                    </td>
                    <td>
                      <BrioDatePicker value={form.newExpenseDate || new Date().toISOString().split('T')[0]}
                        onChange={(v) => setForm({...form, newExpenseDate: v})}
                        openAbove
                        style={{ width: '100%', maxWidth: '95px', fontSize: '10px', boxSizing: 'border-box' }}
                        className="brio-datepicker-trigger--compact" />
                    </td>
                    <td>
                      <select value={form.newExpenseStatus || 'planned'} onChange={(e) => setForm({...form, newExpenseStatus: e.target.value})}
                        className="brio-btn brio-btn-secondary" style={cellInput}>
                        <option value="planned">Planned</option>
                        <option value="committed">Committed</option>
                        <option value="invoiced">Invoiced</option>
                        <option value="paid">Paid</option>
                      </select>
                    </td>
                    <td>
                      <select value={form.newExpenseLocation || 'NL'} onChange={(e) => setForm({...form, newExpenseLocation: e.target.value})}
                        className="brio-btn brio-btn-secondary" style={cellInput}>
                        <option value="NL">NL</option>
                        <option value="IN">IN</option>
                      </select>
                      <label
                        title="Vendor established outside the office country (triggers reverse charge for SaaS, ads, professional services)"
                        style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 9, marginTop: 2, cursor: 'pointer', color: 'var(--brio-text-secondary)' }}>
                        <input type="checkbox" checked={!!form.newExpenseForeignVendor}
                          onChange={(e) => setForm({...form, newExpenseForeignVendor: e.target.checked})}
                          style={{ margin: 0 }} />
                        Foreign
                      </label>
                    </td>
                    <td></td>
                    <td className="text-center">
                      <button
                        onClick={async () => {
                          if (!form.newExpenseProject || !form.newExpenseCategory || !form.newExpenseDesc || !form.newExpenseAmount) {
                            alert('Please fill all fields');
                            return;
                          }

                          const projectId = Number(form.newExpenseProject);
                          if (!Number.isFinite(projectId) || projectId <= 0) {
                            alert('Please select a valid project');
                            return;
                          }

                          const amountOriginal = parseFloat(form.newExpenseAmount);
                          if (!Number.isFinite(amountOriginal) || amountOriginal <= 0) {
                            alert('Please enter a valid amount');
                            return;
                          }

                          const currencyOriginal = inputCurrency;
                          const expenseDate = form.newExpenseDate || new Date().toISOString().split('T')[0];

                          // ── Compute amount_in_inr ──
                          let fxRateToInr = 1;
                          let amountInInr = amountOriginal;

                          if (currencyOriginal === 'EUR') {
                            if (!getFxRate) {
                              alert('Currency settings not loaded. Please wait and try again.');
                              return;
                            }
                            fxRateToInr = getFxRate(expenseDate);
                            if (!fxRateToInr || fxRateToInr <= 1) {
                              alert(`No valid FX rate available for ${expenseDate.substring(0, 7)}. Please add rates in Settings → FX rates.`);
                              return;
                            }
                            amountInInr = Math.round(amountOriginal * fxRateToInr * 100) / 100;
                          }

                          const expenseData = {
                            projectId,
                            category: form.newExpenseCategory,
                            description: form.newExpenseDesc,
                            amount: amountInInr,                // legacy column = amount_in_inr
                            date: expenseDate,
                            status: form.newExpenseStatus || 'planned',
                            officeLocation: form.newExpenseLocation || 'NL',
                            amountOriginal,
                            currencyOriginal,
                            fxRateToInr,
                            amountInInr,
                            foreignVendor: !!form.newExpenseForeignVendor,
                          };

                          if (useSupabaseDB) {
                            const result = await supabaseExpenses.createExpense(expenseData);
                            if (!result.success) { alert('Error creating expense: ' + result.error); return; }
                          } else {
                            setProjectExpenses([...projectExpenses, expenseData]);
                          }

                          setForm({
                            ...form,
                            newExpenseProject: '',
                            newExpenseCategory: '',
                            newExpenseDesc: '',
                            newExpenseAmount: '',
                            newExpenseDate: '',
                            newExpenseStatus: 'planned',
                            newExpenseLocation: 'NL',
                            newExpenseCurrency: 'INR',
                            newExpenseForeignVendor: false,
                          });
                        }}
                        className="brio-btn brio-btn-primary"
                        style={{ padding: '4px 12px', fontSize: '11px' }}
                      >
                        Add
                      </button>
                    </td>
                  </tr>
                    );
                  })()}
                </tbody>
              </BrioTable>
            </BrioByCode>

            {/* ── Totals summary ── */}
            {filteredCombined.filter(e => !e._isLinkedReceipt).length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, padding: '8px 4px', marginTop: 6, fontSize: 11 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: 280 }}>
                  <span style={{ color: '#888' }}>Gross expenses ({filteredCombined.filter(e => !e._isLinkedReceipt).length})</span>
                  <span style={{ fontWeight: 600 }}>{formatMoney(grossTotal, displayCurrency)}</span>
                </div>
                {clientDeduction > 0 && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: 280 }}>
                      <span style={{ color: 'var(--brio-teal, #2a9d8f)' }}>Less: client reimbursements ({clientReimbursements.length})</span>
                      <span style={{ fontWeight: 600, color: 'var(--brio-teal, #2a9d8f)' }}>−{formatMoney(clientDeduction, displayCurrency)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: 280, borderTop: '1px solid var(--brio-border, #d4d0c8)', paddingTop: 4, marginTop: 1 }}>
                      <span style={{ fontWeight: 700 }}>Net company cost</span>
                      <span style={{ fontWeight: 700 }}>{formatMoney(grossTotal - clientDeduction, displayCurrency)}</span>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeExpenses.length === 0 && (
              <div className="brio-text-center brio-analytics-muted" style={{ padding: '32px 0' }}>
                No project expenses yet. Use the table above to add expenses or import from CSV.
              </div>
            )}
          </BrioAnalyticsSurface>

        </div>
      </BrioFrame>
    </div>
  );
}
